/**
 * src/v2/services/spatialBindingModel.ts
 * The rules a binding UI has to know, with no I/O so they can be tested.
 *
 * Everything here mirrors the two contracts rather than inventing policy:
 *
 *   geo-org-registry     can_own_objects     = ACTIVE && identity_verified
 *                        can_operate_objects = ACTIVE && authority_verified
 *                        can_receive_funds   = ACTIVE && beneficiary_verified
 *
 *   geo-spatial-registry propose_binding  rejects PROPOSED and APPROVED,
 *                                         requires only that the org exists
 *                        approve_binding  requires PROPOSED or SUSPENDED,
 *                                         plus can_own_objects and a
 *                                         matching country
 *                        propose_reassign requires APPROVED or SUSPENDED
 *
 * The asymmetry in the middle is the whole reason this file exists. An
 * organization can be *proposed* as owner while only partly verified, and
 * the approval is what fails. A UI that blocked the proposal would be
 * stricter than the chain; a UI that stayed silent would set up a refusal
 * the operator cannot explain. So we allow it and say what will happen.
 *
 * None of this enforces anything. The contract's `require_auth` and its
 * eligibility reads are the security boundary; these functions decide
 * which button to show and what to warn about.
 */

import type {
  Organization,
  OrgCapabilities,
  OrgVerificationDimension,
} from './organizationService';
import {
  BINDING_STATUS_NAME,
  OBJECT_STATUS_NAME,
  RELATIONSHIP,
  RELATIONSHIP_LABEL,
  type BindingStatusName,
  type ObjectStatusName,
  type RelationshipName,
  type SpatialObjectRecord,
} from './spatialRegistryService';

// ── Eligibility ──────────────────────────────────────────────────────────────

export interface OrgEligibility extends OrgCapabilities {
  /**
   * Where the three booleans came from.
   *
   * `contract` — read back from the registry's own eligibility functions.
   * `derived`  — recomputed from the V2 verification dimensions because the
   *              backend did not send `capabilities`.
   * `legacy`   — the deployed registry predates the V2 upgrade, so only the
   *              single identity flag is knowable and the other two are
   *              reported false rather than guessed.
   */
  source: 'contract' | 'derived' | 'legacy';
  /** Dimensions that are false and are therefore holding a capability back. */
  missing: OrgVerificationDimension[];
  /** True when the organization is not ACTIVE, which fails all three. */
  blockedByStatus: boolean;
}

/**
 * The three capabilities for one organization.
 *
 * Prefers the contract's own answer. The fallback recomputes the same
 * expressions rather than approximating them, so the only case that
 * degrades is a pre-V2 registry, and that one degrades to "unknown, so
 * false" instead of to an optimistic guess.
 */
export function deriveEligibility(org: Organization | null | undefined): OrgEligibility {
  const active = org?.statusName === 'active';
  const v2 = org?.verificationV2 ?? null;

  const missing: OrgVerificationDimension[] = [];
  if (v2) {
    if (!v2.identityVerified) missing.push('identityVerified');
    if (!v2.authorityVerified) missing.push('authorityVerified');
    if (!v2.beneficiaryVerified) missing.push('beneficiaryVerified');
  }

  if (org?.capabilities) {
    return {
      ...org.capabilities,
      source: 'contract',
      missing,
      blockedByStatus: !active,
    };
  }

  if (v2) {
    return {
      canOwnObjects: active && v2.identityVerified,
      canOperateObjects: active && v2.authorityVerified,
      canReceiveFunds: active && v2.beneficiaryVerified,
      source: 'derived',
      missing,
      blockedByStatus: !active,
    };
  }

  // Pre-V2 registry: the legacy boolean is the identity dimension and
  // nothing else is recorded, so the other two are unknowable. Reporting
  // them false is the honest reading — the contract will refuse them.
  const legacyIdentity = !!org?.verification?.verified;
  return {
    canOwnObjects: active && legacyIdentity,
    canOperateObjects: false,
    canReceiveFunds: false,
    source: 'legacy',
    missing: legacyIdentity ? [] : ['identityVerified'],
    blockedByStatus: !active,
  };
}

export const DIMENSION_LABEL: Record<OrgVerificationDimension, string> = {
  identityVerified: 'Identity',
  authorityVerified: 'Authority',
  beneficiaryVerified: 'Beneficiary',
};

/**
 * What an operator should be told before they commit, in plain sentences.
 *
 * Returns an empty array when nothing is limited, so the caller can render
 * the block conditionally without a separate "is anything wrong" check.
 */
export function describeLimitations(
  org: Organization | null | undefined,
  eligibility: OrgEligibility = deriveEligibility(org),
): string[] {
  const out: string[] = [];
  if (!org) return out;

  if (org.statusName === 'revoked') {
    out.push('This organization is revoked. No binding to it can be approved.');
    return out;
  }
  if (org.statusName === 'suspended') {
    out.push(
      'This organization is suspended, so every capability is withheld until it '
      + 'is reactivated. A binding can still be proposed.',
    );
  } else if (org.statusName === 'pending') {
    out.push(
      'This organization is still pending, so it cannot yet be approved as an '
      + 'owner. A binding can be proposed now and approved once it is active.',
    );
  }

  if (!eligibility.canOwnObjects) {
    out.push(
      'It may be recorded as the proposed owner, but the binding cannot be '
      + 'approved until identity is verified.',
    );
  }
  if (!eligibility.canOperateObjects) {
    out.push(
      'It cannot operate this object — execute rules or manage operators — '
      + 'until organization authority is verified.',
    );
  }
  if (!eligibility.canReceiveFunds) {
    out.push(
      'It cannot be routed value for this object until beneficiary '
      + 'verification is granted. This is separate from ownership.',
    );
  }

  if (eligibility.source === 'legacy') {
    out.push(
      'This registry predates the three-dimension upgrade, so only identity '
      + 'is knowable here. Upgrade the organization registry wasm to manage '
      + 'authority and beneficiary verification.',
    );
  }
  return out;
}

// ── Country ──────────────────────────────────────────────────────────────────

/**
 * Whether an organization may own a given object at all.
 *
 * The registry compares the two numeric ISO codes and raises
 * `CountryMismatch` at approval, so this is the same test done early.
 * Unknown on either side is not a match: guessing would produce a proposal
 * that can never be approved.
 */
export function countryMatches(
  orgNumericCode: number | null | undefined,
  objectNumericCode: number | null | undefined,
): boolean {
  if (!orgNumericCode || !objectNumericCode) return false;
  return orgNumericCode === objectNumericCode;
}

// ── Which action a bind button should actually perform ───────────────────────

export type BindActionKind =
  /** Not on chain yet: register_object, then propose_binding. */
  | 'register'
  /** No binding, or one that was removed: propose_binding. */
  | 'propose'
  /** A proposal for this same organization exists: approve_binding. */
  | 'approve'
  /** Suspended binding for this same organization: approve_binding reinstates. */
  | 'reinstate'
  /** Held by a different organization: propose_reassign, never a plain bind. */
  | 'reassign'
  /** A transfer is already in flight: complete or cancel it first. */
  | 'complete_reassign'
  /** Already bound to this organization and approved. */
  | 'already_bound'
  /** The chain would refuse. `reason` says why. */
  | 'blocked';

export interface BindAction {
  kind: BindActionKind;
  /** Why, in a sentence an operator can act on. Empty for the plain paths. */
  reason: string;
  /** True when the object must be registered on chain before anything else. */
  needsRegistration: boolean;
  /** The organization currently holding the object, if any. */
  currentOrganizationId: string | null;
  /** True when the current organization comes from an ancestor object. */
  inherited: boolean;
}

/**
 * Decide what binding `targetOrgId` to `record` actually means right now.
 *
 * Mirrors the contract's guards in the order the contract applies them, so
 * the UI never offers a button whose transaction is already known to fail.
 * The `reassign` case is the important one: a generic "bind" over a live
 * binding is exactly the silent overwrite the two-step transfer exists to
 * prevent.
 */
export function classifyBindAction(
  record: SpatialObjectRecord | null | undefined,
  targetOrgId: string | null | undefined,
): BindAction {
  const target = (targetOrgId || '').trim().toLowerCase();

  const base = {
    reason: '',
    needsRegistration: false,
    currentOrganizationId: null as string | null,
    inherited: false,
  };

  if (!record) {
    return {
      ...base,
      kind: 'register',
      needsRegistration: true,
      reason:
        'This object is not in the spatial registry yet. It has to be registered '
        + 'on chain before an organization can be bound to it.',
    };
  }

  const inheritedOrg =
    !record.binding && record.effectiveOrganizationId ? record.effectiveOrganizationId : null;
  const ctx = {
    ...base,
    currentOrganizationId: record.binding?.organizationId ?? inheritedOrg,
    inherited: !!inheritedOrg,
  };

  if (record.statusName === 'retired') {
    return {
      ...ctx,
      kind: 'blocked',
      reason: 'This object is retired. Its binding can no longer be changed.',
    };
  }

  if (record.pendingTransfer) {
    return {
      ...ctx,
      kind: 'complete_reassign',
      reason:
        'A reassignment is already in flight for this object. Complete or cancel '
        + 'it before proposing another binding.',
    };
  }

  const binding = record.binding;
  const status: BindingStatusName = binding?.statusName ?? 'none';
  const holder = (binding?.organizationId || '').toLowerCase();
  const sameOrg = !!target && holder === target;

  if (!binding || status === 'none' || status === 'removed') {
    return {
      ...ctx,
      kind: 'propose',
      reason: inheritedOrg
        ? 'This object currently inherits its organization from its parent. A '
          + 'direct binding will override that inheritance.'
        : '',
    };
  }

  if (status === 'proposed') {
    if (sameOrg) return { ...ctx, kind: 'approve' };
    return {
      ...ctx,
      kind: 'blocked',
      reason:
        'A binding to a different organization is already proposed for this '
        + 'object. Remove that proposal before proposing another.',
    };
  }

  if (status === 'suspended') {
    if (sameOrg) {
      return {
        ...ctx,
        kind: 'reinstate',
        reason: 'The existing binding to this organization is suspended. Approving reinstates it.',
      };
    }
    return {
      ...ctx,
      kind: 'reassign',
      reason:
        'This object is still held by another organization under a suspended '
        + 'binding. Moving it is a reassignment, not a new binding.',
    };
  }

  // approved
  if (sameOrg) return { ...ctx, kind: 'already_bound' };
  return {
    ...ctx,
    kind: 'reassign',
    reason:
      'This object already has an approved organization. Replacing it is a '
      + 'two-step reassignment that clears every delegated operator on completion.',
  };
}

// ── Backend projection vs chain ──────────────────────────────────────────────

export type ReconciliationState =
  /** GeoLink and the chain name the same organization. */
  | 'in_sync'
  /** The chain has a binding GeoLink has not recorded. */
  | 'chain_only'
  /** GeoLink names an organization the chain does not. */
  | 'geolink_only'
  /** Both name an organization, and they disagree. */
  | 'divergent'
  /** Neither names one. */
  | 'unbound';

/**
 * Compare what the chain says against what GeoLink's mirror says.
 *
 * These are two separate stores that were written at different times, so
 * they will disagree during indexing and can disagree permanently if a
 * mirror write failed. Naming the difference is better than picking one
 * and calling it the truth: the chain is authoritative, but a stale mirror
 * is what makes an object disappear from an organization's list.
 *
 * Compares against the *effective* organization so an object that inherits
 * a correctly-mirrored parent binding is not reported as divergent.
 */
export function reconciliationState(
  record: SpatialObjectRecord | null | undefined,
  geoLinkOrganizationId: string | null | undefined,
): ReconciliationState {
  const chain = (
    record?.binding?.organizationId
    || record?.effectiveOrganizationId
    || ''
  ).toLowerCase();
  const mirror = (geoLinkOrganizationId || '').trim().toLowerCase();

  if (!chain && !mirror) return 'unbound';
  if (chain && !mirror) return 'chain_only';
  if (!chain && mirror) return 'geolink_only';
  return chain === mirror ? 'in_sync' : 'divergent';
}

export const RECONCILIATION_MESSAGE: Record<ReconciliationState, string> = {
  in_sync: '',
  unbound: '',
  chain_only:
    'The chain records an organization for this object but GeoLink does not. '
    + 'Search and list views will not find it under that organization until the '
    + 'mirror is written.',
  geolink_only:
    'GeoLink records an organization for this object but the chain does not. '
    + 'The chain is authoritative, so this object is currently unowned on chain.',
  divergent:
    'GeoLink and the chain name different organizations for this object. The '
    + 'chain is authoritative; the GeoLink value is stale.',
};

// ── Labels ───────────────────────────────────────────────────────────────────

const RELATIONSHIP_BY_NUMBER: Record<number, RelationshipName> = Object.fromEntries(
  (Object.keys(RELATIONSHIP) as RelationshipName[]).map((name) => [RELATIONSHIP[name], name]),
);

export function relationshipLabel(value: number | RelationshipName | null | undefined): string {
  if (value === null || value === undefined) return 'None';
  const name = typeof value === 'string' ? value : RELATIONSHIP_BY_NUMBER[value];
  if (!name) return `Unknown (${value})`;
  return RELATIONSHIP_LABEL[name] ?? name;
}

export const BINDING_STATUS_LABEL: Record<BindingStatusName, string> = {
  none: 'Not bound',
  proposed: 'Proposed',
  approved: 'Approved',
  suspended: 'Suspended',
  removed: 'Removed',
};

export const OBJECT_STATUS_LABEL: Record<ObjectStatusName, string> = {
  none: 'Not registered',
  active: 'Active',
  suspended: 'Suspended',
  retired: 'Retired',
};

export function bindingStatusLabel(value: number | BindingStatusName | null | undefined): string {
  if (value === null || value === undefined) return BINDING_STATUS_LABEL.none;
  const name = typeof value === 'string' ? value : BINDING_STATUS_NAME[value];
  return BINDING_STATUS_LABEL[name] ?? BINDING_STATUS_LABEL.none;
}

export function objectStatusLabel(value: number | ObjectStatusName | null | undefined): string {
  if (value === null || value === undefined) return OBJECT_STATUS_LABEL.none;
  const name = typeof value === 'string' ? value : OBJECT_STATUS_NAME[value];
  return OBJECT_STATUS_LABEL[name] ?? OBJECT_STATUS_LABEL.none;
}

/** One line summarising who holds an object, for a list row. */
export function summarizeBinding(record: SpatialObjectRecord | null | undefined): string {
  if (!record) return 'Not registered on chain';
  if (record.binding && record.binding.statusName !== 'removed') {
    return `${relationshipLabel(record.binding.relationship)} · ${bindingStatusLabel(
      record.binding.statusName,
    )}`;
  }
  if (record.effectiveOrganizationId) return 'Inherited from parent';
  return 'No organization bound';
}
